import { useMemo } from "react";
import { LocationService } from "@/services/LocationService";
import { RoomService } from "@/services/RoomService";
import { useQueries } from "@tanstack/react-query";
import type { Location } from "@/types/Location";
import type { PaginatedResponse } from "@/types/Pagination";
import type { Room } from "@/types/Room";

interface UseRoomViewQueryOptions {
  locationSlug: string | undefined;
  roomId: string | undefined;
}

interface RoomViewData {
  location: Location | null | undefined;
  allLocations: Location[];
  room: Room | null | undefined;
  roomsInLocation: Room[];
}

/**
 * Hook to fetch all data needed for the RoomView component.
 * Fetches location, room, all locations (for breadcrumb), and all rooms in location (for breadcrumb).
 *
 * @param options - locationSlug and roomId from route params
 * @returns Query results with isLoading, isError, and data properties
 */
export function useRoomViewQuery(options: UseRoomViewQueryOptions) {
  const { locationSlug, roomId } = options;

  const locationService = useMemo(() => new LocationService(), []);
  const roomService = useMemo(() => new RoomService(), []);

  const results = useQueries({
    queries: [
      // Query 1: Fetch all locations (for breadcrumb dropdown)
      {
        queryKey: ["locations", "serialized"],
        queryFn: (): Promise<PaginatedResponse<Location>> => locationService.getSerializedLocations(),
        enabled: true,
        staleTime: 10 * 60 * 1000, // 10 minutes - locations don't change often
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      // Query 2: Fetch current location
      {
        queryKey: ["location", locationSlug],
        queryFn: () => locationService.getLocationById(locationSlug!),
        enabled: !!locationSlug,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      // Query 3: Fetch all rooms for the location (for breadcrumb dropdown)
      {
        queryKey: ["rooms", "location", locationSlug],
        queryFn: () => roomService.getAllRoomsForLocation(locationSlug!),
        enabled: !!locationSlug,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      // Query 4: Fetch current room
      {
        queryKey: ["room", roomId],
        queryFn: () => roomService.getRoomById(roomId!),
        enabled: !!roomId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
        retry: 2,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    ],
  });

  const [locationsQuery, locationQuery, roomsQuery, roomQuery] = results;

  // Combine loading states - only show loading if any required query is loading
  const isLoading = locationsQuery.isLoading || locationQuery.isLoading || roomsQuery.isLoading || roomQuery.isLoading;

  // Combine error states
  const isError = locationsQuery.isError || locationQuery.isError || roomsQuery.isError || roomQuery.isError;

  // Combine errors for debugging
  const errors = [locationsQuery.error, locationQuery.error, roomsQuery.error, roomQuery.error].filter(Boolean);

  const allLocations: Location[] = locationsQuery.data?.data ?? [];

  // Combine data
  const data: RoomViewData = {
    location: locationQuery.data,
    allLocations: allLocations,
    room: roomQuery.data,
    roomsInLocation: roomsQuery.data || [],
  };

  return {
    isLoading,
    isError,
    error: errors.length > 0 ? errors : null,
    data,
    // Expose individual queries for fine-grained control if needed
    queries: {
      locations: locationsQuery,
      location: locationQuery,
      rooms: roomsQuery,
      room: roomQuery,
    },
  };
}
